import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import ForwardIcon from '@mui/icons-material/Forward';
import { Box, Grid, IconButton, Typography, useTheme } from '@mui/material';
import { APIEndpoint } from 'common/enum';
import { universalPostRequest } from 'common/requestHandler';
import { getUsers } from 'common/utils';
import { defaultErrorMessage } from 'config/config';
import { popupNotification } from 'notifications/ToastNotification';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from 'redux/store';
import { validateGroupName } from 'validation/validationScheme';

import { getUserInterface } from './utils';
import CustomModal from '../../components/CustomModal';
import GradientButton from '../../components/elements/GradientButton';
import HeaderComp from '../../components/elements/HeadingComponent';
import CustomIconButton from '../../components/elements/IconButton';
import InputElement from '../../components/elements/InputElement';
import SelectableList from '../../components/ListItemsWithSearch';
import useSelectableItems from '../../hooks/useSelectableList';

interface groupNameInterface {
  groupName: string;
}
const CreateStaffGroup = () => {
  const [usersList, setUsersList] = useState<getUserInterface[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const { companyId, employeeId } = useSelector(
    (state: RootState) => state.auth
  );
  const navigate = useNavigate();
  const theme = useTheme();

  const { control, handleSubmit, reset } = useForm<groupNameInterface>({
    resolver: yupResolver(validateGroupName),
    mode: 'onChange',
    defaultValues: {
      groupName: '',
    } as groupNameInterface,
  });

  const handleBackClick = () => {
    navigate(-1);
  };

  const getUsersList = async () => {
    setLoading(true);
    const response = await getUsers(companyId, 'GetStaff', employeeId);
    if ('error' in response) {
      popupNotification(defaultErrorMessage, false);
    } else {
      setUsersList(response.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    getUsersList();
  }, [companyId]);

  // State for the left and right lists
  const [leftItems, setLeftItems] = useState<getUserInterface[]>(usersList);
  const [rightItems, setRightItems] = useState<getUserInterface[]>([]);

  // Custom hooks for selecting items on both lists
  const {
    selectedItems: selectedLeftItems,
    toggleItem: toggleLeftItem,
    resetSelection: resetLeftSelection,
  } = useSelectableItems();
  const {
    selectedItems: selectedRightItems,
    toggleItem: toggleRightItem,
    resetSelection: resetRightSelection,
  } = useSelectableItems();

  useEffect(() => {
    setLeftItems(usersList); // Update leftItems whenever usersList changes
  }, [usersList]);

  // Handler to move selected items from left to right
  const handleMoveItemsToRight = () => {
    const updatedRightItems = [
      ...rightItems,
      ...leftItems.filter((item) => selectedLeftItems.includes(item.id)),
    ];
    const updatedLeftItems = leftItems.filter(
      (item) => !selectedLeftItems.includes(item.id)
    );
    // Update the state
    setLeftItems(updatedLeftItems);
    setRightItems(updatedRightItems);
    // Clear the selection in the left list
    resetLeftSelection();
  };
  const createGroup = async (data: groupNameInterface) => {
    setCreatingGroup(true);
    const employeeId = rightItems.map((item) => item.id);
    const payload = {
      companyStaffGroupName: data.groupName,
      companyId,
      employeeId,
      actionName: 'CreateStaffGroup',
    };
    const response = await universalPostRequest(
      APIEndpoint.createStaffGroup,
      payload
    );
    setCreatingGroup(false);
    setRightItems([]);
    setShowModal(false);
    if ('error' in response) {
      popupNotification(response.error, false);
    } else {
      popupNotification(response.message, true);
      getUsersList();
      resetLeftSelection();
      resetRightSelection();
    }
    reset();
  };

  return (
    <>
      <HeaderComp>Create staff group</HeaderComp>

      {/* Back Button */}
      <Box
        display="flex"
        alignItems="center"
        mb={4}
        sx={{
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateX(-4px)',
          },
        }}
        onClick={handleBackClick}
      >
        <IconButton
          size="small"
          sx={{
            backgroundColor: 'rgba(18, 112, 220, 0.1)',
            color: theme.palette.primary.main,
            mr: 1,
            '&:hover': {
              backgroundColor: 'rgba(18, 112, 220, 0.2)',
              transform: 'scale(1.1)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography
          variant="body2"
          color="primary"
          sx={{
            fontWeight: 500,
            userSelect: 'none',
          }}
        >
          Back
        </Typography>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={5}>
          <Box display="flex" flexDirection="column" height="100%" gap={2}>
            {/* Left List */}
            <Typography variant="body1" fontWeight="bold" color="textDisabled">
              Staff users
            </Typography>
            <SelectableList
              items={leftItems}
              selectedItems={selectedLeftItems}
              toggleItem={toggleLeftItem}
              searchVisible={true}
              labelKey="name"
              idKey="id"
              loading={loading}
            />
          </Box>
        </Grid>

        {/* Arrow Button */}
        <Grid item xs={2}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <CustomIconButton
              tooltipTitle="Move selected items"
              buttonType="primary"
              onClick={handleMoveItemsToRight} // Move items on click
            >
              <ForwardIcon color="primary" fontSize="large" />
            </CustomIconButton>
          </Box>
        </Grid>

        <Grid item xs={5}>
          <Box display="flex" flexDirection="column" height="100%" gap={2}>
            {/* Right List */}
            <Typography variant="body1" fontWeight="bold" color="textDisabled">
              Group members
            </Typography>
            <SelectableList
              items={rightItems}
              selectedItems={selectedRightItems}
              toggleItem={toggleRightItem}
              searchVisible={false}
              showCheckBoxIcon={false}
              showDivider={true}
              labelKey="name"
              idKey="id"
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end">
            <GradientButton
              rounded
              disabled={!rightItems.length}
              onClick={() => setShowModal(true)}
            >
              Create group
            </GradientButton>
          </Box>
        </Grid>
      </Grid>
      <CustomModal
        primaryButtonAction={handleSubmit(createGroup)}
        primaryButtonLabel="Create group"
        autoClose={false}
        open={showModal}
        primaryButtonLoading={creatingGroup}
        type="info"
        onClose={() => {
          setShowModal(false);
          reset();
        }}
        title="Staff group name"
      >
        <form onSubmit={handleSubmit(createGroup)}>
          <Typography variant="body2" color="textDisabled">
            {' '}
            Type the staff group name
          </Typography>
          <InputElement
            name="groupName"
            size="small"
            control={control}
            label="Group name"
            autoFocus
            type="text"
            rules={{ required: true }} // Rules can be added here
          />
        </form>
      </CustomModal>
    </>
  );
};

export default CreateStaffGroup;
